import React, { useCallback, useState } from 'react';
import { StyleSheet } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import { BaseAdminScreen, AdminErrorBoundary } from '@/components';
import { useResponsive } from '@/hooks/common/useResponsive';
import { useTodoLists } from '@/hooks/admin/useTodoLists';
import type { AdminStackParamList } from '@/navigation/admin/AdminNavigator';
import { DesktopToDosView } from './desktop/DesktopToDosView';
import { ToDosList } from './mobile/ToDosList';
import type { TodoScope } from './mobile/ToDosList';

/**
 * Admin To Do's index. Responsive host mirroring the existing admin screens
 * (e.g. SurveyResponsesScreen): the desktop layout renders the desktop view,
 * everything else renders the mobile list inside BaseAdminScreen.
 *
 * The My/All lists toggle drives `useTodoLists(scope)`. `mine` is active lists
 * assigned to you or a role you hold; `all` is every list for the property —
 * active AND archived — and is staff-gated server-side (the admin app is the
 * staff surface, so the toggle renders unconditionally). Archived lists carry no
 * `currentOccurrence`, so their rows show the muted "Archived" treatment with no
 * progress. Rows navigate to the occurrence detail; active rows also expose a
 * row-level Edit affordance straight to the edit form.
 */
export const ToDosScreen: React.FC = () => {
  const { layoutMode } = useResponsive();
  const [scope, setScope] = useState<TodoScope>('mine');
  const { todoLists, isLoading } = useTodoLists(scope);
  const navigation = useNavigation<StackNavigationProp<AdminStackParamList>>();

  const onSelectList = useCallback(
    (listId: number) => {
      navigation.navigate('TodoListOccurrence', { listId });
    },
    [navigation],
  );

  const onEditList = useCallback(
    (listId: number) => {
      navigation.navigate('EditTodoList', { listId });
    },
    [navigation],
  );

  const onCreateList = useCallback(() => {
    navigation.navigate('CreateTodoList');
  }, [navigation]);

  if (layoutMode === 'desktop') {
    return (
      <AdminErrorBoundary>
        <DesktopToDosView
          lists={todoLists}
          isLoading={isLoading}
          scope={scope}
          onScopeChange={setScope}
          onSelectList={onSelectList}
          onCreateList={onCreateList}
          onEditList={onEditList}
        />
      </AdminErrorBoundary>
    );
  }

  return (
    <AdminErrorBoundary>
      <BaseAdminScreen
        screenName="ToDos"
        showPlaceholder={false}
        scrollEnabled={false}
        contentContainerStyle={styles.baseScreenContent}
      >
        <ToDosList
          lists={todoLists}
          isLoading={isLoading}
          scope={scope}
          onScopeChange={setScope}
          onSelectList={onSelectList}
          onCreateList={onCreateList}
          onEditList={onEditList}
        />
      </BaseAdminScreen>
    </AdminErrorBoundary>
  );
};

const styles = StyleSheet.create({
  baseScreenContent: {
    paddingHorizontal: 0,
    paddingTop: 0,
    paddingBottom: 0,
    flexGrow: 1,
  },
});
